import { Autocomplete, Chip, ClickAwayListener, IconButton, MenuItem, Paper, Popper, Select, Skeleton, TextField, Typography } from "@mui/material";
import { Box, Stack, styled } from "@mui/system";
import { DataGridPro, GridColDef, GridColumnVisibilityModel, GridPagination, GridRenderCellParams, useGridApiContext } from "@mui/x-data-grid-pro";
import React, { useCallback, useMemo } from "react";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router";
import { CountryFilter, useCountryData } from "src/components/competitive-intelligence";
import CustomTooltip from "src/components/competitive-intelligence/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { useGetDomainsQuery, useGetIndustriesQuery, useGetBrandsQuery } from "src/context/api/brandPulse/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { formatCurrency } from "src/utils/format-currency";
import { formatNumber, fPercent } from "src/utils/format-number";

export default function Brands() {
  const { countriesData, isLoading: isLoadingCountries } = useCountryData();
  const [activeCountry, setActiveCountry] = useState<string | null>(null);
  const navigate = useNavigate();
  const apiRef: any = useRef();
  const workspace: any = useCurrentWorkspace();
  const workspaceId: any = workspace?.id;
  const lang = localStorage.getItem('i18nextLng') || 'en';

  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const toolbarRef: any = useRef();

  useEffect(() => {
    if (countriesData && countriesData.length > 0 && !activeCountry) {
      setActiveCountry(countriesData[0].country_name);
    }
  }, [countriesData, activeCountry]);

  const CustomFooter = ({
    paginationModel,
    onPageSizeChange
  }: any) => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          backgroundColor: "#FAFAFA",
          px: 2,
          py: 1,
          gap: 2,
          borderBottomRightRadius: "8px",
          borderBottomLeftRadius: "8px",

        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>Rows per page:</Typography>
          <Select
            size="small"
            value={paginationModel.pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            sx={{ minWidth: 80 }}
          >
            {[5, 10, 20, 50].map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>

          <GridPagination />
        </Stack>
      </Box>
    );
  };

  const CustomToolbar = (props: any) => {
    const { data: domainsData }: any = useGetDomainsQuery(
      {
        workspace_id: workspaceId,
        country: activeCountry
      },
      {
        skip: !workspaceId || !activeCountry,
      }
    );

    const { data: industriesData }: any = useGetIndustriesQuery(
      {
        workspace_id: workspaceId,
        country: activeCountry
      },
      {
        skip: !workspaceId || !activeCountry,
      }
    );

    const domains = domainsData?.domains || [];
    const industries = industriesData?.industries || [];

    return (
      <Stack
        ref={toolbarRef}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={0}
        px={2.5}
        py={1.25}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: 'white',
          borderTopRightRadius: '8px',
          borderTopLeftRadius: '8px',
          borderBottom: '1px dashed rgba(145, 158, 171, 0.20)',
        }}
      >

        <Stack>
          <Typography fontWeight={600} fontSize={"16px"} color={"#333C47"}>Domains</Typography>
        </Stack>

        <Stack direction="row" alignItems="center" px={1} spacing={0.5}>
          <Stack direction="row" spacing={2}>
            <Autocomplete
              multiple
              disablePortal
              options={industries}
              value={selectedIndustries}
              onChange={(event, newValue) => setSelectedIndustries(newValue)}
              getOptionLabel={(option) => option}
              size="small"
              sx={{ width: 220 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {selectedIndustries.length === 0 && (
                          <Chip
                            label="Industries"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(145, 158, 171, 0.32)',
                              color: 'rgba(51, 60, 71, 1)',
                              mr: 0.5,
                              ":hover": {
                                backgroundColor: 'rgba(145, 158, 171, 0.62)',
                              }
                            }}
                          />
                        )}

                        {selectedIndustries.length === 1 && (
                          <Chip
                            label={selectedIndustries[0]}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(145, 158, 171, 0.32)',
                              color: 'rgba(51, 60, 71, 1)',
                              mr: 0.5,
                              ":hover": {
                                backgroundColor: 'rgba(145, 158, 171, 0.62)',
                              }
                            }}
                          />
                        )}

                        {selectedIndustries.length > 1 && (
                          <>
                            <Chip
                              label={selectedIndustries[0]}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(145, 158, 171, 0.32)',
                                color: 'rgba(51, 60, 71, 1)',
                                mr: 0.5,
                                ":hover": {
                                  backgroundColor: 'rgba(145, 158, 171, 0.62)',
                                }
                              }}
                            />
                            <Chip
                              label={`+${selectedIndustries.length - 1}`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(145, 158, 171, 0.32)',
                                color: 'rgba(51, 60, 71, 1)',
                                mr: 0.5,
                                ":hover": {
                                  backgroundColor: 'rgba(145, 158, 171, 0.62)',
                                }
                              }}
                            />
                          </>
                        )}

                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Autocomplete
              multiple
              disablePortal
              options={domains}
              value={selectedDomains}
              onChange={(event, newValue) => setSelectedDomains(newValue)}
              getOptionLabel={(option) => option}
              size="small"
              sx={{ width: 220 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {selectedDomains.length === 0 && (
                          <Chip
                            label="Domains"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(145, 158, 171, 0.32)',
                              color: 'rgba(51, 60, 71, 1)',
                              mr: 0.5,
                              ":hover": {
                                backgroundColor: 'rgba(145, 158, 171, 0.62)',
                              }
                            }}
                          />
                        )}

                        {selectedDomains.length === 1 && (
                          <Chip
                            label={selectedDomains[0]}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(145, 158, 171, 0.32)',
                              color: 'rgba(51, 60, 71, 1)',
                              mr: 0.5,
                              ":hover": {
                                backgroundColor: 'rgba(145, 158, 171, 0.62)',
                              }
                            }}
                          />
                        )}

                        {selectedDomains.length > 1 && (
                          <>
                            <Chip
                              label={selectedDomains[0]}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(145, 158, 171, 0.32)',
                                color: 'rgba(51, 60, 71, 1)',
                                mr: 0.5,
                                ":hover": {
                                  backgroundColor: 'rgba(145, 158, 171, 0.62)',
                                }
                              }}
                            />
                            <Chip
                              label={`+${selectedDomains.length - 1}`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(145, 158, 171, 0.32)',
                                color: 'rgba(51, 60, 71, 1)',
                                mr: 0.5,
                                ":hover": {
                                  backgroundColor: 'rgba(145, 158, 171, 0.62)',
                                }
                              }}
                            />
                          </>
                        )}

                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

          </Stack>

          <IconButton
            onClick={() => apiRef.current?.showPreferences("columns")}
            sx={{
              width: 26,
              height: 26,
              padding: 0,
              color: '#919EAB',
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          >
            <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
          </IconButton>
          <IconButton
            onClick={() => apiRef.current?.showFilterPanel()}
            sx={{
              width: 26,
              height: 26,
              padding: 0,
              color: '#919EAB',
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          >
            <Iconify icon="ic:round-filter-list" width={18} height={18} />
          </IconButton>
          <IconButton
            onClick={() => apiRef.current?.exportDataAsCsv({
              fileName: 'brands-pulse',
              utf8WithBom: true
            })}
            sx={{
              width: 24,
              height: 24,
              padding: 0,
              color: '#919EAB',
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          >
            <Iconify icon="lets-icons:export" width={16} height={16} />
          </IconButton>

        </Stack>
      </Stack>
    )
  }

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    workspace_name: false,
    blended_cpc_activated: false,
    blended_ctr_activated: false,
    ad_cost_activated: false,
    blended_conversions_total: false,
  });

  const handleChangeColumnVisibilityModel = React.useCallback((newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(newModel);
  }, []);


  const columns: GridColDef[] = [
    {
      field: 'rank',
      headerName: 'Rank',
      type: "number",
      width: 60,
      renderCell: (params) => (
        <Stack spacing={1} direction="row" alignItems="center" sx={{ px: 1, width: 1, height: 1 }}>
          {params.row.data_status
            ? <Typography fontSize={"14px"} color={"rgba(47, 56, 67, 1)"}>
              #{params.row.rank}
            </Typography>
            : <CustomTooltip title="Tracking in progress, it might take 48h hours to complete" placement="top"><Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                width: 40,
                height: 40,
                transition: "all 0.2s ease",
                position: "relative",
                "&:hover": {
                  cursor: "pointer",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: -2,
                    left: -4,
                    right: -4,
                    bottom: -2,
                    borderRadius: "6px",
                    background: "rgba(118, 53, 220, 0.08)",
                  },
                },
              }}
            >
              <Iconify icon="svg-spinners:bars-rotate-fade" width={24} height={24} />
            </Box>
            </CustomTooltip>
          }
        </Stack>
      ),
    },
    {
      field: 'domains',
      filterable: false,
      flex: 1,
      headerName: 'Domains',
      renderCell: (params) => (
        <Stack spacing={1} direction="row" alignItems="center" justifyContent={"space-between"} sx={{ px: 1, height: 1, width: "100%" }}>
          <Box display={"flex"} flexDirection={"row"}>
            <Box
              sx={{
                width: 24,
                height: 24,
                border: "1px solid rgba(129, 129, 129, 0.2)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1,
              }}
            >
              <img
                src={`https://www.google.com/s2/favicons?domain=${params.row.domains}&sz=32`}
                alt="favicon"
                style={{ width: 16, height: 16 }}
              />
            </Box>
            <Typography fontSize={"14px"} color={"rgba(47, 56, 67, 1)"}>{params.row.domains}</Typography>
          </Box>

          {(params.row.domains === clientDomain || workspace?.friendlyDomains?.includes(params.row.domains)) &&
            <Box sx={{ backgroundColor: "rgba(145, 158, 171, 0.10)", padding: "2px 6px", borderRadius: "6px" }}>
              <Typography fontSize={"12px"} color={"rgba(118, 53, 220, 1)"}>
                Your brand
              </Typography>
            </Box>
          }

        </Stack>
      ),
    },
    {
      field: 'industry',
      filterable: false,
      minWidth: 180,
      headerName: 'Industry',
      renderCell: (params) => (
        <>
          {params.row.data_status &&
            <Stack spacing={1} direction="row" alignItems="center" sx={{ px: 1, width: 1, height: 1 }}>
              <Box sx={{ backgroundColor: "rgba(171, 204, 237, 0.20)", padding: "2px 6px", borderRadius: "6px" }}>
                <Typography onClick={() => setSelectedIndustries([params.row.industry])} fontSize={"14px"} color={"rgba(23, 64, 127, 1)"} sx={{ ":hover": { textDecoration: "underline", cursor: 'pointer' } }}>
                  {params.row.industry}
                </Typography>
              </Box>
            </Stack>
          }
        </>
      ),
    },
    {
      field: 'monthly_search_volume',
      headerName: 'Monthly Search Volume',
      minWidth: 200,
      type: 'number',
      headerAlign: 'right',
      align: 'right',
      renderHeader: (params) => (
        <Box textAlign={"right"} sx={{ color: "rgba(99, 115, 129, 1)" }}>
          <Typography fontWeight={'600'} fontSize={"14px"}>Monthly</Typography>
          <Typography fontWeight={'600'} fontSize={"14px"}>Search volume</Typography>

        </Box>
      ),
      renderCell: (params) => (
        <>
          {params.row.data_status &&
            <Stack spacing={1} direction="row" alignItems="center" justifyContent={"flex-end"} sx={{ px: 1, width: 1, height: 1 }}>
              <Typography fontSize={"14px"}>
                {formatNumber(params.row.monthly_search_volume)}
              </Typography>
            </Stack>
          }
        </>
      ),
    },
    {
      field: 'organic_vs_paid',
      flex: 1,
      filterable: false,
      headerName: 'Org. vs Paid vol.',
      align: 'center',
      renderCell: (params) => {
        const [organic, paid] = params.row.organic_vs_paid
          .split(' / ')
          .map((v: string) => parseFloat(v) || 0);

        const total = organic + paid;
        const organicPercent = total ? (organic / total) * 100 : 0;
        const paidPercent = total ? (paid / total) * 100 : 0;

        return (
          <>
            {params.row.data_status &&
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ width: '100%', height: "100%" }}
              >
                <Typography color={"rgba(47, 56, 67, 1)"} variant="caption" sx={{ minWidth: 30, textAlign: 'right' }}>
                  {organic}%
                </Typography>

                <Box
                  sx={{
                    position: 'relative',
                    flexGrow: 1,
                    height: 8,
                    border: "none",
                    borderRadius: 1,
                    backgroundColor: 'white',
                    display: 'flex',
                  }}
                >
                  <Box
                    sx={{
                      width: `${organicPercent}%`,
                      backgroundColor: '#1EC5D0',
                      borderRadius: "2px",
                    }}
                  />

                  <Box
                    sx={{
                      width: `${paidPercent}%`,
                      backgroundColor: '#F6C6C6',
                      borderRadius: "2px",
                      ml: '2px',
                    }}
                  />
                </Box>

                <Typography color={"rgba(47, 56, 67, 1)"} variant="caption" sx={{ minWidth: 30, textAlign: 'left' }}>
                  {paid}%
                </Typography>
              </Stack>
            }
          </>
        );
      }

    },

    {
      field: 'monthly_spend_estimate',
      headerName: 'Monthly Spend Estimate',
      type: "number",
      flex: 1,
      renderHeader: (params) => (
        <Box sx={{ color: "rgba(99, 115, 129, 1)" }}>
          <Typography fontWeight={'600'} fontSize={"14px"}>Monthly spend</Typography>
          <Typography fontWeight={'600'} fontSize={"14px"}>estimate</Typography>

        </Box>
      ),
      renderCell: (params) => (
        <>
          {params.row.data_status &&
            <Stack spacing={1} direction="row" alignItems="center" justifyContent={"flex-start"} sx={{ px: 1, width: 1, height: 1 }}>
              <Typography fontSize={"14px"}>
                {formatCurrency(params.row.monthly_spend_estimate, workspace?.currency, lang, true, 1)}
              </Typography>
            </Stack>
          }
        </>
      ),
    },
    {
      field: 'visibility_score',
      flex: 1,
      type: "number",
      headerName: 'Visibility score',
      renderCell: (params: GridRenderCellParams) => {
        const colorRange = [
          {
            min: 0,
            max: 30,
            color: "#F5CE47",
          },
          {
            min: 30,
            max: 70,
            color: "#BC9510",
          },
          {
            min: 70,
            max: 100,
            color: "#18A155",
          },
        ]

        const color = colorRange.find(c => params.row.visibility_score >= c.min && params.row.visibility_score <= c.max)?.color || "success.main";

        return (
          <>
            {params.row.data_status &&
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ width: '100%', height: "100%" }}
              >
                <Typography variant="body2">{fPercent(params.row.visibility_score)}</Typography>
                <Stack direction="row" alignItems="center" spacing={1} py={1} sx={{ width: "100%", height: "52px" }}>
                  <Stack sx={{ width: `${params.row.visibility_score}%`, height: "8px", backgroundColor: color, borderRadius: "6px" }} />
                </Stack>
              </Stack>
            }
          </>
        )
      },
    },
  ]

  const GridPanelRoot = styled(Popper, {
    name: 'MuiDataGrid',
    slot: 'Panel',
    overridesResolver: (props, styles) => styles.panel
  })(({
    theme
  }) => ({
    zIndex: (theme as any).zIndex.modal
  }));
  const GridPaperRoot = styled(Paper, {
    name: 'MuiDataGrid',
    slot: 'Paper',
    overridesResolver: (props, styles) => styles.paper
  })(({
    theme
  }) => ({
    backgroundColor: "#FFFFFF",
    minWidth: 300,
    maxHeight: 450,
    display: 'flex',
    maxWidth: `calc(100vw - ${theme.spacing(0.5)})`,
    overflow: 'auto'
  }));

  const CustomNoRowsOverlay = () => {
    return (
      <Stack
        height="100%"
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: 'white',
          color: '#637381',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(229, 218, 253, 0.40)',
              p: '12px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Iconify color={"rgba(118, 85, 193, 1)"} icon="clarity:paste-line" width={48} height={48} />
          </Box>
        </Box>

        <Typography fontSize={'16px'} color={"rgba(51, 60, 71, 1)"} variant="subtitle1" fontWeight={600} mt={"12px"}>
          No selected brands
        </Typography>
        <Box mt={"12px"} display={"flex"} flexDirection={"row"}>
          <Typography color={"rgba(99, 115, 129, 1)"} fontSize={'14px'} textAlign={"center"} variant="body2">
            You haven’t Selected any competitors currently, go to <br />
            <Box
              component="span"
              sx={{
                color: "rgba(81, 25, 183, 1)",
                cursor: "pointer",
                fontSize: "16px",
                textDecoration: "underline",
                marginRight: .6, // MUI spacing scale (4px * 1)
                "&:hover": {
                  color: "rgba(81, 25, 183, 0.75)"
                }
              }}
              onClick={() => navigate("/brand-pulse/competitors-selection")}
            >
              Competitors Selection
            </Box>
            tab to select few brands and track them.
          </Typography>
        </Box>
      </Stack>
    );
  };

  const { data: brandsData, isLoading: isBrandsLoading, isFetching } = useGetBrandsQuery(
    {
      workspace_id: workspaceId,
      country: activeCountry || "",
      industries: selectedIndustries,
      domains: selectedDomains
    },
    {
      skip: !workspaceId || !activeCountry,
      refetchOnMountOrArgChange: true,
    }
  );

  const clientDomain = workspace?.url || "";

  const rows = useMemo(() => {
    if (!brandsData?.data) return [];

    const sortedData = [...brandsData.data];
    const clientDomain = workspace?.url || "";
    const friendlyDomains: string[] = workspace?.friendlyDomains || [];
    const lowerFriendlyDomains = friendlyDomains.map(d => d.toLowerCase());

    const clientRowIndex = sortedData.findIndex(
      r => r.domain_name.toLowerCase() === clientDomain.toLowerCase() &&
        r.region_name === activeCountry
    );

    let clientRow: any = null;
    if (clientRowIndex >= 0) {
      clientRow = sortedData.splice(clientRowIndex, 1)[0];
    }

    const friendlyRows: any[] = [];
    for (let i = sortedData.length - 1; i >= 0; i--) {
      if (
        lowerFriendlyDomains.includes(sortedData[i].domain_name.toLowerCase()) &&
        sortedData[i].region_name === activeCountry
      ) {
        friendlyRows.unshift(sortedData.splice(i, 1)[0]);
      }
    }

    const notReadyRows = sortedData.filter(r => r.data_status === false);
    const readyRows = sortedData.filter(r => r.data_status === true);

    const finalRows = [
      ...notReadyRows,
      ...(clientRow ? [clientRow] : []),
      ...friendlyRows,
      ...readyRows
    ].map((brand, index) => ({
      id: index + 1,
      rank: index + 1,
      domains: brand.domain_name,
      data_status: brand.data_status,
      industry: brand.industry,
      monthly_search_volume: brand.monthly_search_volume,
      organic_vs_paid: `${brand.organic_volume || 0} / ${brand.paid_volume || 0}`,
      monthly_spend_estimate: brand.monthly_spend_estimate,
      visibility_score: brand.visibility_score,
    }));

    return finalRows;
  }, [brandsData, workspace?.url, workspace?.friendlyDomains, activeCountry]);

  const hasRows = rows.length > 0;

  const CountryFilterSkeleton = () => {
    return (
      <Paper
        sx={{
          p: 0,
          overflow: "hidden",
          backgroundColor: "transparent",
          borderBottom: "1px solid rgba(145, 158, 171, 0.20)",
          px: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 1,
          }}
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <Box
              key={`country-skeleton-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                minHeight: 36,
              }}
            >
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="text" width={60 + Math.random() * 40} height={20} />
            </Box>
          ))}
        </Box>
      </Paper>
    );
  }


  const isLoading = isLoadingCountries || isBrandsLoading || isFetching || !brandsData;

  return (
    <>
      <Helmet>
        <title>Brand Pulse</title>
      </Helmet>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 13 }}>

        {isLoadingCountries ? (
          <CountryFilterSkeleton />
        ) : (
          countriesData && countriesData.length > 1 && (
            <CountryFilter countries={countriesData} selectedCountry={activeCountry} onCountryChange={(c) => setActiveCountry(c)} />
          )
        )}

        <Box px={'24px'} sx={{ marginTop: "20px", height: '600px' }}>
          {isLoading
            ? <Skeleton width={'100%'} height={700} variant="rectangular" animation="wave" sx={{
              borderRadius: '8px',
            }} />
            : <DataGridPro
              sx={{
                height: "600px",
                border: "1px solid rgba(145, 158, 171, 0.2)",
                borderRadius: '8px',
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                  outline: 'none !important',
                },
                '& .MuiDataGrid-columnHeader, .MuiDataGrid-filler': {
                  backgroundColor: 'rgba(226, 229, 233, 0.16) !important',
                  height: "53px!important",
                  color: "rgba(99, 115, 129, 1)",
                  fontWeight: 'normal!important'
                },
                '& .MuiDataGrid-cell': {
                  border: "1px solid rgba(145, 158, 171, 0.2)",
                  color: 'rgba(51, 60, 71, 1)',
                  backgroundColor: 'white',
                },
                "& .client-row .MuiDataGrid-cell": {
                  backgroundColor: "rgba(0, 143, 205, 0.06)",
                  fontWeight: "bold",
                },
                "& .data-not-ready .MuiDataGrid-cell": {
                  backgroundColor: "rgba(118, 53, 220, 0.08)",
                  fontWeight: "bold",
                },
                width: '100%',

                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'hidden !important',
                },
                '& .MuiDataGrid-main': {
                  overflow: 'hidden !important',
                },
                '& .MuiDataGrid-cellContent': {
                  overflow: 'visible !important',
                },
                '& .highlighted-row': {
                  opacity: 0.3,
                },
                overflow: 'visible !important',
              }}
              getRowClassName={(params) => {
                const classes = [];
                if (params.row.domains === clientDomain) classes.push("client-row");
                if (params.row.data_status === false) classes.push("data-not-ready");
                return classes.join(" ");
              }}
              apiRef={apiRef}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleChangeColumnVisibilityModel}
              columns={columns}
              autoHeight={!hasRows ? false : true}

              pagination={hasRows}
              hideFooter={!hasRows}
              rows={rows}
              hideFooterPagination={!hasRows}
              hideFooterSelectedRowCount
              density="comfortable"
              disableRowSelectionOnClick
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              slots={{
                panel(props) {
                  const apiRefContext = useGridApiContext();
                  const [isPlaced, setIsPlaced] = useState(false);

                  const handleClickAway = useCallback(() => {
                    apiRefContext.current.hidePreferences();
                  }, [apiRefContext]);

                  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
                    if (event.key === 'Escape') {
                      apiRefContext.current.hidePreferences();
                    }
                  }, [apiRefContext]);

                  const modifiers = useMemo(() => [{
                    name: 'flip',
                    enabled: true,
                    options: {
                      rootBoundary: 'document'
                    }
                  }, {
                    name: 'isPlaced',
                    enabled: true,
                    phase: 'main' as const,
                    fn: () => {
                      setIsPlaced(true);
                    },
                    effect: () => () => {
                      setIsPlaced(false);
                    }
                  }] as any, []);

                  const open = apiRef.current?.state?.preferencePanel?.open;
                  const panelType = apiRef.current?.state?.preferencePanel?.openedPanelValue;
                  const anchorEl = toolbarRef.current;

                  if (!anchorEl) {
                    return null;
                  }

                  const panelProps = {
                    ...props,
                    as: undefined,
                    anchorEl: anchorEl,
                    open: open,
                  };

                  return (
                    <GridPanelRoot
                      {...panelProps}
                      placement="bottom-end"
                      className={props.className}
                      modifiers={modifiers}
                    >
                      <ClickAwayListener
                        mouseEvent="onPointerUp"
                        touchEvent={false}
                        onClickAway={handleClickAway}
                      >
                        <GridPaperRoot
                          elevation={8}
                          onKeyDown={handleKeyDown}
                        >
                          {isPlaced && props.children}
                        </GridPaperRoot>
                      </ClickAwayListener>
                    </GridPanelRoot>
                  );
                },
                toolbar: CustomToolbar,
                noRowsOverlay: CustomNoRowsOverlay,
                footer: hasRows
                  ? () => (
                    <CustomFooter
                      paginationModel={paginationModel}
                      onPageSizeChange={(newSize: number) =>
                        setPaginationModel((prev) => ({
                          ...prev,
                          pageSize: newSize,
                          page: 0,
                        }))
                      }
                    />
                  )
                  : undefined,
              }}
            />
          }
        </Box>
      </Box>
    </>
  )
}